import type { SupportedCurrency } from '../utils/currency';
import {
    BILLING_SUBSCRIPTION_QUERY_STATUSES,
    isCanceledBillingSubscriptionStatus,
    isCurrentBillingSubscriptionStatus,
    selectCurrentBillingSubscription,
    selectLatestCanceledSubscription,
} from './currentBillingSubscription';
import { supabase, toError } from './supabase';

export type BillingPlanId = 'starter' | 'plus' | 'pro';

export type PersonalBillingSubscription = {
    planId: BillingPlanId;
    status: string;
    currency: SupportedCurrency | null;
    billingInterval: 'month' | 'year' | null;
    priceLookupKey: string | null;
    currentPeriodEnd: string | null;
    cancelAt: string | null;
    cancelAtPeriodEnd: boolean;
    canceledAt: string | null;
};

export type PersonalBillingState = {
    billingCurrency: SupportedCurrency | null;
    subscription: PersonalBillingSubscription | null;
    latestCanceledSubscription: PersonalBillingSubscription | null;
};

const supportedCurrencySet = new Set(['USD', 'EUR', 'GBP', 'CNY']);
const supportedPlanSet = new Set(['starter', 'plus', 'pro']);
const db = supabase as any;

export function normalizeBillingCurrency(value: unknown): SupportedCurrency | null {
    if (typeof value !== 'string') {
        return null;
    }

    const normalized = value.trim().toUpperCase();
    return supportedCurrencySet.has(normalized)
        ? normalized as SupportedCurrency
        : null;
}

function normalizeBillingPlanId(value: unknown): BillingPlanId | null {
    if (typeof value !== 'string') {
        return null;
    }

    const normalized = value.trim().toLowerCase();
    return supportedPlanSet.has(normalized)
        ? normalized as BillingPlanId
        : null;
}

function normalizeBillingInterval(value: unknown): 'month' | 'year' | null {
    return value === 'month' || value === 'year' ? value : null;
}

function normalizeSubscription(row: Record<string, unknown>): PersonalBillingSubscription | null {
    const planId = normalizeBillingPlanId(row.plan_id);
    const status = typeof row.status === 'string' ? row.status.trim() : '';

    if (
        !planId
        || (!isCurrentBillingSubscriptionStatus(status) && !isCanceledBillingSubscriptionStatus(status))
    ) {
        return null;
    }

    return {
        planId,
        status,
        currency: normalizeBillingCurrency(row.currency),
        billingInterval: normalizeBillingInterval(row.billing_interval),
        priceLookupKey: typeof row.price_lookup_key === 'string' && row.price_lookup_key.trim()
            ? row.price_lookup_key.trim()
            : null,
        currentPeriodEnd: typeof row.current_period_end === 'string'
            ? row.current_period_end
            : null,
        cancelAt: typeof row.cancel_at === 'string' ? row.cancel_at : null,
        cancelAtPeriodEnd: row.cancel_at_period_end === true,
        canceledAt: typeof row.canceled_at === 'string' ? row.canceled_at : null,
    };
}

function chooseCurrentSubscription(
    rows: PersonalBillingSubscription[],
): PersonalBillingSubscription | null {
    return selectCurrentBillingSubscription(rows);
}

export async function fetchPersonalOrganizationBillingCurrency(
    userId: string,
): Promise<SupportedCurrency | null> {
    const billingState = await fetchPersonalOrganizationBillingState(userId);
    return billingState.billingCurrency;
}

export async function fetchPersonalOrganizationBillingState(
    userId: string,
): Promise<PersonalBillingState> {
    const { data: organizations, error: organizationError } = await db
        .from('organizations')
        .select('id')
        .eq('owner_id', userId)
        .eq('is_personal', true)
        .limit(1);

    if (organizationError) {
        throw toError(organizationError);
    }

    const organizationId = organizations?.[0]?.id;
    if (typeof organizationId !== 'string' || !organizationId) {
        return {
            billingCurrency: null,
            subscription: null,
            latestCanceledSubscription: null,
        };
    }

    const { data: subscriptions, error: subscriptionError } = await db
        .from('subscriptions')
        .select('plan_id,status,currency,billing_interval,price_lookup_key,current_period_end,cancel_at,cancel_at_period_end,canceled_at,updated_at')
        .eq('organization_id', organizationId)
        .in('status', [...BILLING_SUBSCRIPTION_QUERY_STATUSES])
        .order('updated_at', { ascending: false })
        .limit(10);

    if (subscriptionError) {
        throw toError(subscriptionError);
    }

    const normalizedSubscriptions = Array.isArray(subscriptions)
        ? subscriptions
            .map((row: Record<string, unknown>) => normalizeSubscription(row))
            .filter((row: PersonalBillingSubscription | null): row is PersonalBillingSubscription => row !== null)
        : [];
    const billingCurrency = normalizedSubscriptions.find((row) =>
        row.currency && isCurrentBillingSubscriptionStatus(row.status)
    )?.currency ?? null;

    return {
        billingCurrency,
        subscription: chooseCurrentSubscription(normalizedSubscriptions),
        latestCanceledSubscription: selectLatestCanceledSubscription(normalizedSubscriptions),
    };
}
